<style scoped lang="scss">
    .padding-bottom { padding-bottom: 0.5rem; }
    .constraint-item {
        border: 1px solid #ddd;
        border-radius: 10px;
        padding-left: 12px;
        margin-bottom: 0.5em;
    }
</style>
<template>
    <Modal :title='dateId?date.formatKapitelDate(dateId, "d. MMMM yyyy"):"Tagesdetails"' v-model="isOpen">

        <!-- Booking Details -->
        <Skeleton
            v-if="loader.pending.value && expectedContent === 'booking'"
            :items="['block--height-10rem', 'margin', 'title', 'margin', 'block--height-10rem']"
        />
        <BookingDetails v-if="!loader.pending.value && day?.booking" :booking="day.booking"/>


        <!-- Availability Absence Editor -->
        <Skeleton
            v-if="loader.pending.value && expectedContent !== 'booking'"
            :items="['title', 'block--height-4em', 'block--height-4em', 'block--height-4em']"
        />
        <AvailabilityAbsenceEditor
            v-if="!loader.pending.value && !day?.booking"
            :availability="availability"
            :absence="day?.absence ?? undefined"
            :date="dateId"
            @set:availability="onSetAvailability"
            @set:absence="onSetAbsence"
        />


        <!-- Additional Availabilities || Demand Suggestions -->
        <Skeleton
            v-if="loader.pending.value && expectedContent !== 'booking'"
            :items="['margin', 'title', 'line', 'line', 'button']"
        />
        <template v-if="!loader.pending.value && !day?.booking">

            <h3 style="padding-top: 2em">ZUSÄTZLICH VERFÜGBAR FÜR OFFENE DIENSTE</h3>

            <template v-if="(additionalAvailabilityRS || demandSuggestionsRS)">
                <p v-if="additionalAvailabilityRS" class="text-medium">
                    Du bist
                    <IonLabel color="primary"><strong>zusätzlich verfügbar</strong></IonLabel>
                    für {{pluralizeHuman(additionalAvailabilityRS.count, 'einen offenen Dienst', 'offene Dienste')}} im
<!--                    <IonChip class="small" style="&#45;&#45;background: var(&#45;&#45;ion-color-primary); &#45;&#45;color: white; font-weight: bold; margin-right: 0">{{additionalAvailabilityRS.shiftLabel}}</IonChip>-->
                    <span style="font-weight: bold">{{additionalAvailabilityRS.shiftLabel}}</span>
                    auf {{additionalAvailabilityRS.jobsiteLabel}}.
                </p>
                <p v-if="demandSuggestionsRS" class="text-medium">
                    {{additionalAvailabilityRS && demandSuggestionsRS ? 'Zudem gibt es noch' : 'Es gibt'}} <span>{{pluralizeHuman(demandSuggestionsRS.count, 'einen Dienstvorschlag', 'Dienstvorschläge')}}</span> im {{demandSuggestionsRS.shiftLabel}} auf {{demandSuggestionsRS.jobsiteLabel}}.
                </p>


                <IonButton color="light" @click="() => showAdditionalAvailabilityEditor = true" class="padding-bottom">
                    <template v-if="additionalAvailabilityRS">
                        <IonIcon slot="start" :icon="KapitelIcon.edit"/>
                        bearbeiten
                    </template>
                    <template v-else>
                        Vorschläge ansehen
                        <IonIcon slot="end" :icon="KapitelIcon.caret"/>
                    </template>
                </IonButton>
            </template>
            <template v-else>
                <IonButton color="light" @click="() => kapitelDate ? askChatForDemandOn(kapitelDate) : null">
                    <IonIcon :icon="KapitelIcon.chat" slot="start"/>
                    Weitere offene Dienste suchen
                </IonButton>
            </template>
            <AdditionalAvailabilityEditor
                v-if="!!day"
                :day="day"
                :additionalAvailabilitySummary="day?.availability?.additionalAvailabilityCoordinates"
                :demandSuggestions="demandSuggestionsRS?.moreDemands"
                v-model="showAdditionalAvailabilityEditor"
            />
        </template>


        <!-- Register Bookings -->
        <Skeleton
            v-if="loader.pending.value && expectedContent !== 'booking'"
            :items="['margin', 'title', 'button']"
        />
        <template v-if="!loader.pending.value && !day?.booking">

            <h3 style="padding-top: 2em">WEITERES</h3>
            <IonButton color="light" @click="showRegisterBooking = true">
                Abgesprochenen Dienst eintragen
            </IonButton>
            <RegisterBookingModal
                :modelValue="showRegisterBooking"
                :date-preselection="dateId"
                :date-min="dateId ? startOfMonth(dateId) : undefined"
                :date-max="dateId ? endOfMonth(dateId) : undefined"
            />
        </template>
    </Modal>
</template>

<script setup lang="ts">
import {computed, PropType, Ref, ref, watch} from 'vue';
import {generateCalendarDateFromKapitelDateString} from '@/helper/calendar/calendarDate';
import {
    additionalAvailabilitySummaryLabel, generateAdditionalAvailabilityRenderSet,
    generateAvailability,
    hasAdditionalAvailabilities
} from '@/helper/availability';
import {fetchDayDetailedForDate, fetchDayForDate} from '@/helper/day';
import BookingDetails from '@/views/CalendarDetail/components/BookingDetails.vue';
import AvailabilityAbsenceEditor from '@/components/AvailabilityAbsenceEditor.vue';
import {DataLoader} from '@/helper/dataLoader';
import Skeleton from '@/components/Skeleton.vue';
import Modal from "@/components/Modal.vue";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import date, {endOfMonth, startOfMonth} from "@/helper/datetime/date";
import {Absence, AInesAssistantType, Availability, Day, DemandJoDaShTi} from "@/graphql/generated/graphql";
import {IonButton, IonIcon, IonItem, IonLabel, IonChip} from "@ionic/vue"
import RegisterBookingModal from "@/views/components/RegisterBookingModal.vue";
import {getShortJobsiteDescription} from "@/helper/jobsite"
import {KapitelIcon} from "@/helper/icon";
import datetime from "@/helper/datetime/datetime";
import {getRandomArrayElement} from "@/helper/array";
import {pluralize, pluralizeHuman} from "@/helper/amountFormatter";
import {sendUserMessage, setUpAssistant} from "@/helper/chat/chatBL";
import {useGlobalEmitter} from "@/helper/emitter";
import {
    addAvailabilityCoordinateToDay,
    askChatForDemandOn,
    removeAvailabilityCoordinateFromDay
} from "@/helper/demandBrowser";
import AdditionalAvailabilityEditor from "@/components/AdditionalAvailabilityEditor.vue";
import {generateDemandSuggestionsRenderSet} from "@/helper/demand";

const emit = defineEmits(['set:availability', 'set:absence'])

const onSetAvailability = (availability:Availability) => {
    emit("set:availability", availability, false)
}
const onSetAbsence = (absence:Absence) => {
    emit("set:absence", absence, false)
}
export type ExpectedContent = "booking" | "absence" | "availability" | "demandSuggestions" | "empty" | undefined

const props = defineProps({
    expectedContent: {
        type: String as PropType<ExpectedContent>,
        required: false,
        default: undefined
    },
});


const dateId: Ref<kapitelDateString|undefined> = defineModel({type: String as PropType<kapitelDateString>})
const isOpen : Ref<Boolean> = ref(!!dateId.value)

watch(()=>dateId.value, value=>
{
    isOpen.value=!!value
    if(isOpen.value){
        if(day.value){
            loader.reload()
        }else{
            loader.load()
        }
    }
})
watch(()=>isOpen.value, value=>{
    if(!value){
        dateId.value = undefined
    }
} )

const showRegisterBooking = ref(false)
const showAdditionalAvailabilityEditor = ref(false)

const day : Ref<Day|undefined> = ref();
const availability = ref();

const kapitelDate = computed(() => {
    if(dateId.value){
        const calendarDate = generateCalendarDateFromKapitelDateString(dateId.value);
        return calendarDate.id
    }
    return undefined
});

const loader = new DataLoader([
    async () => {
        if(!kapitelDate.value){
            return
        }
        day.value = await fetchDayDetailedForDate(
            kapitelDate.value
        );
        availability.value = day.value?.availability || generateAvailability(kapitelDate.value);
    }
]);
useGlobalEmitter().on('day:mutated', (date) => {
    if (dateId.value === date) {
        loader.load()
    }
})

if (isOpen.value) {
    loader.load()
}

const reload = () => {
    debugger
    loader.load();
};

defineExpose({
    reload
});



const additionalAvailabilityRS = computed(() => {

    if (availability.value && hasAdditionalAvailabilities(availability.value)) {
        return generateAdditionalAvailabilityRenderSet(availability.value)
    }
    return undefined
})
const demandSuggestionsRS = computed(() => {
    if (day.value?.moreDemands && day.value?.moreDemands.length > 0) {
        return {
            moreDemands: day.value?.moreDemands,
            ...generateDemandSuggestionsRenderSet(day.value?.moreDemands)
        }
    }
    return undefined
})

</script>
